import type { AgentComponentType } from '@reach/shared-types/agent-components';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../../../components/common/Modal';
import { Button } from '../../../components/ui/button';
import { newComponentPath } from '../agent-component-paths';
import { COMPONENT_TYPE_COPY, STATUS_COPY } from '../agents-copy';
import { StatusNote } from '../StatusNote';
import { useAgentComponentList } from '../useAgentComponents';
import styles from './AddComponentDialog.module.css';
import { ComponentOptionRow } from './ComponentOptionRow';

interface AddComponentDialogProps {
  type: AgentComponentType;
  heading: string;
  templateComponentIds: string[];
  onAdd: (componentIds: string[]) => void;
  onClose: () => void;
}

export const AddComponentDialog = ({
  type,
  heading,
  templateComponentIds,
  onAdd,
  onClose,
}: AddComponentDialogProps) => {
  const [selectedComponentIds, setSelectedComponentIds] = useState<string[]>([]);
  const {
    data: partnerComponents = [],
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAgentComponentList(type);
  const { title: typeTitle, newLabel, emptyNote, loadErrorNote } = COMPONENT_TYPE_COPY[type];
  const addableComponents = partnerComponents.filter(
    component => !templateComponentIds.includes(component.id),
  );
  const componentsAreAddable = addableComponents.length > 0;
  const listIsComplete = !isLoading && !isError && !hasNextPage;
  const partnerHasNoComponents = listIsComplete && partnerComponents.length === 0;
  const everyComponentIsOnTemplate = listIsComplete && !partnerHasNoComponents && !componentsAreAddable;
  const loadMoreLabel = isFetchingNextPage ? STATUS_COPY.loading : STATUS_COPY.loadMore;
  const everyComponentIsOnTemplateNote = `Everything in ${typeTitle} is already on this template.`;
  const componentsAreSelected = selectedComponentIds.length > 0;
  const addSelectedLabel = componentsAreSelected
    ? `Add ${selectedComponentIds.length} to template`
    : 'Add to template';

  const toggleComponent = (componentId: string) =>
    setSelectedComponentIds(current =>
      current.includes(componentId)
        ? current.filter(id => id !== componentId)
        : [...current, componentId],
    );

  const addSelectedComponents = () =>
    onAdd(
      addableComponents
        .filter(component => selectedComponentIds.includes(component.id))
        .map(component => component.id),
    );

  return (
    <Modal title={heading} onClose={onClose}>
      {isLoading && <StatusNote>{STATUS_COPY.loading}</StatusNote>}
      {isError && <StatusNote tone="error">{loadErrorNote}</StatusNote>}
      {componentsAreAddable && (
        <ul className={styles.componentOptions}>
          {addableComponents.map(component => (
            <ComponentOptionRow
              key={component.id}
              component={component}
              selected={selectedComponentIds.includes(component.id)}
              onToggle={() => toggleComponent(component.id)}
            />
          ))}
        </ul>
      )}
      {partnerHasNoComponents && <p className={styles.empty}>{emptyNote}</p>}
      {everyComponentIsOnTemplate && <p className={styles.empty}>{everyComponentIsOnTemplateNote}</p>}
      {hasNextPage && (
        <button
          type="button"
          className={styles.loadMore}
          disabled={isFetchingNextPage}
          onClick={() => fetchNextPage()}>
          {loadMoreLabel}
        </button>
      )}
      <footer className={styles.actions}>
        <Link to={newComponentPath(type)} className={styles.createComponent}>
          {newLabel}
        </Link>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {componentsAreAddable && (
          <Button type="button" disabled={!componentsAreSelected} onClick={addSelectedComponents}>
            {addSelectedLabel}
          </Button>
        )}
      </footer>
    </Modal>
  );
};
